import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function HomeCaseStudy() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          sectionRef.current?.querySelectorAll('.hcs-fade').forEach((el, i) => {
            setTimeout(() => {
              (el as HTMLElement).classList.add('opacity-100', 'translate-y-0');
              (el as HTMLElement).classList.remove('opacity-0', 'translate-y-8');
            }, i * 120);
          });
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-28 bg-[#F7F9FC]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="hcs-fade opacity-0 translate-y-8 transition-all duration-700 mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-[#1B2D5B] text-xs tracking-[0.3em] mb-4 uppercase font-medium">/ Case Study</p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D1B2A] tracking-tight leading-tight">
              制作・支援実績
            </h2>
          </div>
          <Link
            to="/case-study"
            className="inline-flex items-center gap-2 text-sm text-[#1B2D5B] font-medium hover:gap-4 transition-all whitespace-nowrap group"
          >
            実績一覧を見る
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Case teaser card */}
        <div
          className="hcs-fade opacity-0 translate-y-8 transition-all duration-700 rounded-3xl overflow-hidden border border-[#E2E8F0]"
          style={{ background: 'linear-gradient(135deg, #F7F9FC 0%, #EEF2F9 100%)' }}
        >
          {/* ── Website Preview (full-width top) ── */}
          <div className="relative bg-[#0D1B2A] px-6 md:px-8 pt-6 pb-0 overflow-hidden">
            {/* bg texture */}
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=dark%20navy%20blue%20abstract%20geometric%20texture%20background%2C%20subtle%20glowing%20grid%20lines%2C%20elegant%20deep%20midnight%20blue%20professional%20design%2C%20soft%20light%20beams%2C%20premium%20dark%20backdrop%20for%20business%20presentation%2C%20minimalist%20dark%20blue%20atmosphere%20with%20faint%20bokeh%20lights&width=900&height=600&seq=casestudy-darkbg-01&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-center opacity-30"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#0D1B2A]/60 to-[#0D1B2A]/20" />
            </div>
            {/* browser chrome */}
            <div className="relative z-10 flex items-center gap-2 mb-3">
              <span className="w-3 h-3 rounded-full bg-[#FF5F57] shrink-0" />
              <span className="w-3 h-3 rounded-full bg-[#FEBC2E] shrink-0" />
              <span className="w-3 h-3 rounded-full bg-[#28C840] shrink-0" />
              <div className="flex-1 mx-2 bg-white/10 rounded-md px-3 py-1.5 flex items-center gap-2 max-w-xs">
                <i className="ri-lock-line text-white/40 text-xs" />
                <span className="text-white/40 text-xs font-mono truncate">akihiro-kushikatsu.jp</span>
              </div>
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-white/10 text-white/60 border border-white/10 shrink-0">
                <i className="ri-store-2-line" />
                飲食店 / 熊本市
              </span>
            </div>
            {/* iframe */}
            <div className="relative z-10 rounded-t-xl overflow-hidden w-full h-[320px] bg-white">
              <iframe
                src="https://readdy.cc/preview/41d580cf-b466-4484-b234-e6188398962d/7862685/"
                title="串カツ あき×ひろ ウェブサイト"
                className="absolute top-0 left-0"
                style={{
                  width: '167%',
                  height: '167%',
                  transform: 'scale(0.6)',
                  transformOrigin: 'top left',
                  border: 'none',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </div>

          {/* ── Details ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-[#E2E8F0]">
            {/* Left */}
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs text-[#1B2D5B] font-semibold tracking-widest uppercase">No. 01</span>
                <span className="h-px flex-1 bg-[#1B2D5B]/20" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#0D1B2A] leading-snug mb-1">
                串カツ あき×ひろ 様
              </h3>
              <p className="text-[#1B2D5B] text-sm font-medium mb-4">熊本市 ／ 飲食店</p>
              <div className="flex flex-wrap gap-2 mb-5">
                {['WEBサイト制作', 'MEO対策', '月額保守'].map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1 rounded-full bg-[#1B2D5B]/8 text-[#1B2D5B] border border-[#1B2D5B]/20 font-medium">
                    {tag}
                  </span>
                ))}
              </div>
              <p className="text-sm text-[#555] leading-relaxed">
                Readdy AIを活用したスピード公開、Googleマップ（MEO）対策による認知拡大、月額保守による安定運用の3つのアプローチで集客を最大化。
              </p>
            </div>

            {/* Right */}
            <div className="p-8 md:p-10 flex flex-col justify-between gap-6">
              <div className="flex items-start gap-3 p-5 rounded-2xl bg-[#1B2D5B] text-white">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/15 shrink-0 mt-0.5">
                  <i className="ri-award-line text-base text-white" />
                </div>
                <p className="text-sm font-semibold leading-relaxed">
                  公開後3ヶ月でGoogleマップの露出が大幅増加
                </p>
              </div>
              <Link
                to="/case-study"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-[#1B2D5B] text-[#1B2D5B] text-sm font-semibold hover:bg-[#1B2D5B] hover:text-white transition-colors whitespace-nowrap"
              >
                <i className="ri-eye-line" />
                詳細を見る
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
